import type { PortalAccess } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Field, Input } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { KeyRound, Mail } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';

export const portalAccessQuery = (contactId: string) => queryOptions({
  queryKey: ['portal-access', contactId],
  queryFn: () => api<PortalAccess>(`/contacts/${contactId}/portal-access`),
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const date = (iso: string) => new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/**
 * Acesso do paciente à área do paciente: a equipe envia um link de ativação para o e-mail dele;
 * ao abrir, o paciente cadastra o app autenticador. Reenviar = trocou de celular; revogar = perdeu o celular.
 */
export function PortalAccessBox({ contactId, compact = false }: { contactId: string; compact?: boolean }) {
  const { data: me } = useSuspenseQuery(meQuery);
  const allowed = me.permissions.includes('portal.access.manage');
  const qc = useQueryClient();
  const { data: access } = useQuery({ ...portalAccessQuery(contactId), enabled: allowed });
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const onSaved = (res: PortalAccess) => { qc.setQueryData(portalAccessQuery(contactId).queryKey, res); setEditing(false); setConfirmRevoke(false); };
  const invite = useMutation({
    mutationFn: (to: string) => api<PortalAccess>(`/contacts/${contactId}/portal-access/invite`, { method: 'POST', body: { email: to } }),
    onSuccess: onSaved,
  });
  const revoke = useMutation({
    mutationFn: () => api<PortalAccess>(`/contacts/${contactId}/portal-access/revoke`, { method: 'POST' }),
    onSuccess: onSaved,
  });

  if (!allowed || !access) return null;
  const showForm = editing || !access.email;
  const emailOk = EMAIL_RE.test(email.trim());
  const status = {
    active: <Badge tone="success">Ativo</Badge>,
    invited: <Badge tone="warning" className="shrink-0 whitespace-nowrap">Aguardando ativação</Badge>,
    none: <Badge>Sem acesso</Badge>,
  }[access.status];

  return (
    <Card className={`space-y-3 ${compact ? 'p-3.5 text-[12.5px]' : 'p-4 text-sm'}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-medium text-fg"><KeyRound className="size-4 text-fg-subtle" />Área do paciente</p>
        {status}
      </div>

      {access.status === 'active' && access.enrolledAt && <p className="text-fg-muted">App autenticador cadastrado em {date(access.enrolledAt)}.</p>}
      {access.status === 'invited' && access.inviteExpiresAt && (
        <p className="text-fg-muted">Link enviado{access.invitedAt ? ` em ${date(access.invitedAt)}` : ''}; vale até {date(access.inviteExpiresAt)}.</p>
      )}
      {access.status === 'none' && <p className="text-fg-muted">Envie um link para o e-mail do paciente. Ele ativa o acesso com um app autenticador, sem senha.</p>}

      {showForm ? (
        <form noValidate className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (emailOk) invite.mutate(email); }}>
          <Field label="E-mail do paciente" error={email && !emailOk ? 'Confira o e-mail' : undefined}>
            <Input type="email" inputMode="email" autoComplete="off" placeholder="nome@exemplo.com" value={email}
              onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            <Button size="sm" type="submit" disabled={!emailOk} loading={invite.isPending}><Mail className="size-4" />Enviar link de acesso</Button>
            {access.email && <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>}
          </div>
        </form>
      ) : (
        <div className="space-y-2">
          <p className="flex items-center gap-1.5 break-all text-fg"><Mail className="size-3.5 shrink-0 text-fg-subtle" />{access.email}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" loading={invite.isPending} onClick={() => invite.mutate(access.email!)}>
              {access.status === 'none' ? 'Enviar link de acesso' : 'Reenviar link'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEmail(access.email ?? ''); setEditing(true); }}>Trocar e-mail</Button>
            {access.status !== 'none' && !confirmRevoke && (
              <Button size="sm" variant="ghost" className="text-danger" onClick={() => setConfirmRevoke(true)}>Revogar acesso</Button>
            )}
          </div>
          {access.status === 'active' && <p className="text-fg-muted">Trocou de celular? Reenvie o link: o app antigo deixa de funcionar quando o novo for ativado.</p>}
        </div>
      )}

      {confirmRevoke && (
        <Alert tone="warning" title="Revogar o acesso?">
          <p>O paciente sai de todos os aparelhos e o app autenticador deixa de funcionar. Para voltar, envie um novo link.</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="danger" loading={revoke.isPending} onClick={() => revoke.mutate()}>Revogar</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmRevoke(false)}>Manter</Button>
          </div>
        </Alert>
      )}
      {(invite.error ?? revoke.error) && <Alert tone="danger">{(invite.error ?? revoke.error)!.message}</Alert>}
      {invite.isSuccess && !invite.isPending && <p className="text-success">Link enviado para {access.email}.</p>}
    </Card>
  );
}
